import {
  ApplicationConfig,
  APP_INITIALIZER,
  provideAppInitializer,
  inject,
  PLATFORM_ID,
} from '@angular/core';

import { ErrorHandler, LOCALE_ID, importProvidersFrom } from '@angular/core';
import { environment } from '../environments/environment';
import { LoggerModule } from 'ngx-logger';
import { withInterceptorsFromDi, provideHttpClient, withFetch } from '@angular/common/http';
import { provideToastr } from 'ngx-toastr';

import { provideAnimations } from '@angular/platform-browser/animations';
import { provideFunctions } from '@angular/fire/functions';
import { provideStorage } from '@angular/fire/storage';

import { provideAuth } from '@angular/fire/auth';

import {
  getFirebaseApp,
  getFirebaseAuth,
  getFirebaseStorage,
  getFirebaseFunctions,
} from './firebase/clients';
import { provideFirebaseApp } from '@angular/fire/app';
import { AppCurrencyFormatService } from './app-currency-format.service';
import { CurrencyFormatService } from '@pedix-workspace/pedixapp-shared-pipes';
import { AppRequestContextService } from './app-request-context.service';
import { ModulePreloadManualStrategyService } from '@pedix-workspace/pedixapp-core-services';

import {
  provideRouter,
  withEnabledBlockingInitialNavigation,
  withPreloading,
} from '@angular/router';

import { AppPathLocationStrategy } from './app.path-location-strategy';
import { isPlatformBrowser, LocationStrategy, registerLocaleData } from '@angular/common';
import { AppEnvironmentService } from './app-environment.service';
import {
  EnvironmentService,
  ErrorHandlerService,
  ERROR_HANDLER_CONFIG,
  GlobalErrorHandlerService,
  CustomNGXLoggerServerService,
} from '@pedix-workspace/angular-utils';
import { TOKEN_LOGGER_SERVER_SERVICE } from 'ngx-logger';
import { provideClientHydration } from '@angular/platform-browser';
import { appRoutes } from './app.routes';
import { provideTransloco } from '@ngneat/transloco';
import { TranslocoHttpLoader } from './transloco/transloco-http-loader.module';
import { UiFormReactiveConfigService } from '@pedix-workspace/angular-ui-form-reactive';
import { AppFormReactiveConfigService } from './app-form-reactive-config.service';
import { initializeFirestore, provideFirestore } from '@angular/fire/firestore';
import { HERE_MAPS_SERVICE_API_KEY } from '@pedix-workspace/angular-ui-here-maps';

import { PixelModule } from 'ngx-multi-pixel';
import {
  GOOGLE_MAPS_SERVICE_API_KEY,
  STADIA_MAPS_SERVICE_API_KEY,
} from '@pedix-workspace/angular-ui-map';
import { ErrorProcessorConfig } from 'libs/angular-utils/src/lib/error-handler/error-handler.types';

export const appConfig: ApplicationConfig = {
  providers: [
    provideFirebaseApp(() => getFirebaseApp()),
    provideAuth(() => getFirebaseAuth()),
    provideFirestore(() =>
      initializeFirestore(getFirebaseApp(), { ignoreUndefinedProperties: true }),
    ),
    provideStorage(() => getFirebaseStorage()),
    provideFunctions(() => getFirebaseFunctions()),
    provideAppInitializer(() => {
      const globalErrorHandler = inject(GlobalErrorHandlerService);

      globalErrorHandler.initialize();
    }),
    importProvidersFrom(
      // ServiceWorkerModule.register('ngsw-worker.js', { enabled: environment.production }),
      LoggerModule.forRoot(
        {
          serverLoggingUrl: '/logs',
          enableSourceMaps: true,
          level: environment.logger.logLevel,
          serverLogLevel: environment.logger.serverLogLevel,
          disableConsoleLogging: environment.logger.disableConsoleLogging,
        },
        {
          serverProvider: {
            provide: TOKEN_LOGGER_SERVER_SERVICE,
            useClass: CustomNGXLoggerServerService,
          },
        },
      ),
      PixelModule.forRoot({ enabled: true, pixelId: [environment.pedixPixelId] }),
    ),
    { provide: EnvironmentService, useClass: AppEnvironmentService },
    {
      provide: ERROR_HANDLER_CONFIG,
      useFactory: (platformId, appRequestContextService: AppRequestContextService) => {
        const xLogTrace = isPlatformBrowser(platformId)
          ? new URLSearchParams(window.location.search).get('x-log-trace')
          : undefined;
        return <ErrorProcessorConfig>{
          system: 'pedixapp',
          filters: environment.logger.errorFilters,
          production: environment.production,
          customMetadata: {
            xLogTrace,
            establishmentId: appRequestContextService.establishment?.id,
            slug: appRequestContextService.slug,
          },
        };
      },
      deps: [PLATFORM_ID, AppRequestContextService],
    },
    { provide: ErrorHandler, useClass: ErrorHandlerService },
    { provide: LocationStrategy, useClass: AppPathLocationStrategy },
    { provide: AppPathLocationStrategy, useExisting: LocationStrategy },
    {
      provide: LOCALE_ID,
      useFactory: (appRequestContextService: AppRequestContextService) => {
        registerLocaleData(appRequestContextService.localeData, appRequestContextService.localeId);
        return appRequestContextService.localeId;
      },
      deps: [AppRequestContextService],
    },
    {
      provide: CurrencyFormatService,
      useClass: AppCurrencyFormatService,
    },
    {
      provide: UiFormReactiveConfigService,
      useClass: AppFormReactiveConfigService,
    },
    { provide: HERE_MAPS_SERVICE_API_KEY, useValue: environment.hereMaps.apiKey },
    { provide: GOOGLE_MAPS_SERVICE_API_KEY, useValue: environment.googleMaps.apiKey },
    { provide: STADIA_MAPS_SERVICE_API_KEY, useValue: environment.stadiaMaps.apiKey },
    provideClientHydration(),
    provideAnimations(),
    provideHttpClient(withInterceptorsFromDi(), withFetch()),
    provideRouter(
      appRoutes,
      withEnabledBlockingInitialNavigation(),
      withPreloading(ModulePreloadManualStrategyService),
    ),
    provideTransloco({
      config: {
        availableLangs: ['es-MX', 'es-AR'],
        defaultLang: 'es-MX',
        fallbackLang: 'es-MX',
        // Remove this option if your application doesn't support changing language in runtime.
        reRenderOnLangChange: false,
        prodMode: environment.production,
        missingHandler: {
          // It will use the first language set in the `fallbackLang` property
          useFallbackTranslation: true,
        },
      },
      loader: TranslocoHttpLoader,
    }),
    provideToastr({
      timeOut: 6000,
      positionClass: 'toast-top-center',
      preventDuplicates: true,
      closeButton: true,
    }),
  ],
};
